// The contact form plugin from Great Southern Websites. Put in place by the
// website-builder elf as web/contact/contact-form.js and bundled by the web
// bundler into its own bundle (quarkus.web-bundler.bundle.contact=true);
// templates/partials/contact-form.html loads it on the contact page only.
// It renders in the light DOM so the site's own styles and theme tokens
// apply. Fields and the recipient come from the app at render time, so the
// owner changes them in Settings without a rebuild. Do not edit this file on
// a site: the elf replaces it with its current copy when the form is added.
import { LitElement, html, nothing } from 'lit';

const FAILED = 'Sorry, the message could not be sent. Please try again in a moment.';

class GswContactForm extends LitElement {
  static properties = {
    site: { type: String },
    api: { type: String },
    _config: { state: true },
    _state: { state: true },
    _error: { state: true },
    _sent: { state: true },
  };

  createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    this._state = 'loading';
    this._load();
  }

  _url() {
    return String(this.api || '').replace(/\/+$/, '') + '/v1/public/contact/' + encodeURIComponent(this.site || '');
  }

  async _load() {
    try {
      const res = await fetch(this._url(), { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('unavailable');
      this._config = await res.json();
      this._state = 'ready';
    } catch (e) {
      this._state = 'unavailable';
    }
  }

  async _submit(e) {
    e.preventDefault();
    const body = {};
    for (const [k, v] of new FormData(e.target).entries()) body[k] = v;
    this._state = 'sending';
    this._error = '';
    try {
      const res = await fetch(this._url(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      let out = {};
      try { out = await res.json(); } catch (err) { /* no body */ }
      if (!res.ok) {
        this._error = out && out.error ? out.error : FAILED;
        this._state = 'ready';
        return;
      }
      this._sent = out && out.message ? out.message : 'Thank you, your message has been sent.';
      this._state = 'sent';
    } catch (err) {
      this._error = FAILED;
      this._state = 'ready';
    }
  }

  render() {
    if (this._state === 'loading') return html`<p class="gsw-contact-note">Loading the form...</p>`;
    if (this._state === 'unavailable') {
      return html`<p class="gsw-contact-note">The contact form is not available right now. Please use the phone number or email address on this page.</p>`;
    }
    if (this._state === 'sent') return html`<p class="gsw-contact-sent" role="status">${this._sent}</p>`;
    const sending = this._state === 'sending';
    return html`
      <form class="gsw-contact" @submit=${this._submit}>
        ${this._config.fields.map(f => this._field(f))}
        <div class="gsw-contact-trap" aria-hidden="true">
          <label>Leave this empty<input type="text" name="website" tabindex="-1" autocomplete="off"></label>
        </div>
        ${this._error ? html`<p class="gsw-contact-error" role="alert">${this._error}</p>` : nothing}
        <p><button class="btn btn-primary" type="submit" ?disabled=${sending}>${sending ? 'Sending...' : 'Send message'}</button></p>
      </form>`;
  }

  _field(f) {
    const id = 'gsw-contact-' + f.name;
    const autocomplete = f.name === 'email' ? 'email' : f.name === 'phone' ? 'tel' : f.name === 'name' ? 'name' : 'off';
    const control = f.type === 'textarea'
      ? html`<textarea id=${id} name=${f.name} rows="6" maxlength=${this._config.maxMessageChars || 5000} ?required=${f.required}></textarea>`
      : html`<input id=${id} name=${f.name} type=${f.type} autocomplete=${autocomplete} ?required=${f.required}>`;
    return html`<p class="gsw-contact-field"><label for=${id}>${f.label}${f.required ? '' : ' (optional)'}</label>${control}</p>`;
  }
}

customElements.define('gsw-contact-form', GswContactForm);
